// Core Firebase app/auth/db bootstrap shared by features.

import { initializeApp } from "firebase/app";
import {
  getAuth,
  setPersistence,
  browserLocalPersistence,
  browserSessionPersistence,
} from "firebase/auth";
import { getFirestore, enableIndexedDbPersistence } from "firebase/firestore";

let _app = null, _auth = null, _db = null;
let _ready = false, _reason = "";
let _persistReady = null;

const env = (k) => process.env[k];

function validateEnv() {
  const required = [
    "REACT_APP_FB_API_KEY",
    "REACT_APP_FB_PROJECT_ID",
    "REACT_APP_FB_APP_ID",
  ];
  const missing = required.filter((k) => !String(env(k) || "").trim());
  if (missing.length) {
    _reason = `Missing env vars: ${missing.join(", ")}`;
    return false;
  }
  return true;
}

function host() {
  try { return (typeof window !== "undefined" ? window.location.hostname : "") || ""; } catch { return ""; }
}

// Prefer explicit env; otherwise default to the Firebase-managed domain.
function resolveAuthDomain() {
  const explicit = String(env("REACT_APP_FB_AUTH_DOMAIN") || "").trim();
  if (explicit) return explicit;
  const proj = String(env("REACT_APP_FB_PROJECT_ID") || "").trim();
  return proj ? `${proj}.firebaseapp.com` : "";
}

function initIfNeeded() {
  if (_ready) return true;
  if (!validateEnv()) return false;

  const cfg = {
    apiKey: env("REACT_APP_FB_API_KEY"),
    authDomain: resolveAuthDomain(),
    projectId: env("REACT_APP_FB_PROJECT_ID"),
    storageBucket: env("REACT_APP_FB_STORAGE_BUCKET"),
    messagingSenderId: env("REACT_APP_FB_MESSAGING_SENDER_ID"),
    appId: env("REACT_APP_FB_APP_ID"),
    measurementId: env("REACT_APP_FB_MEASUREMENT_ID"),
  };

  try {
    _app = initializeApp(cfg);
    _auth = getAuth(_app);
    _db = getFirestore(_app);

    // Firestore local persistence for instant repeat queries + offline
    // (ignore if blocked — e.g., Safari Private Mode or tab contention)
    enableIndexedDbPersistence(_db, { synchronizeTabs: true }).catch(() => {});

    // Prefer local; fall back to session when blocked (iOS/Safari Private Mode).
    _persistReady = setPersistence(_auth, browserLocalPersistence).catch(async (e) => {
      console.warn("Auth persistence warning:", e?.code || e?.message, "→ falling back to session");
      await setPersistence(_auth, browserSessionPersistence);
    });

    _ready = true;
    _reason = "";
    return true;
  } catch (e) {
    _ready = false;
    _app = _auth = _db = null;
    _reason = e?.message || "Firebase init failed";
    return false;
  }
}

export async function waitAuthPersistence() {
  try { if (_persistReady) await _persistReady; } catch { }
}

export function getFirebase() {
  return { app: _app, auth: _auth, db: _db, ready: initIfNeeded(), reason: _reason };
}

export function isFirebaseReady() { return initIfNeeded(); }
export function notReadyReason() { return _reason; }
